import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faInstagram,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import {
  faLocationDot,
  faMobileScreenButton,
  faPaperPlane,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import {
  formatDisplayPhone,
  condenseOfficeHours,
  type OfficeHourEntry,
} from '@vibesites/theme-oasis/components/PropertyInfoPanel';

// ============================================================================
// Text color on the gray card
// ----------------------------------------------------------------------------
// The card background is pinned to a static light gray (#f3f4f6) — see
// `.cb-card` CSS below — so we don't need the Footer's WCAG luminance swap
// here. Text on the card is always the dark variant.
// ============================================================================

const TEXT_ON_CARD = '#1a1a1a';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const heroImageDef: SlotGroupDefinition = {
  label: 'Hero Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Contact us background',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// Use the property *standard* (primary) logo here — not a mono variant.
// The card background is a static light gray (#f3f4f6) and the primary
// logo is expected to read against any reasonable background, so we no
// longer flip between mono_black / mono_white based on surface luminance.
// The user can still pick a different variant via the slot picker.
//
// NOTE: keep comments out of this object literal. The schema-linter's
// static extractor uses a regex brace-balancer that does not strip JS
// comments, and treats unmatched apostrophes (e.g. "property's") as
// string openers — which eats subsequent content and breaks extraction
// of the surrounding const, cascading into a contentSchema that cannot
// be statically evaluated at preview time.
const logoDef: SlotGroupDefinition = {
  label: 'Brand Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_primary_logo',
      suggestedFields: [
        'property_primary_logo',
        'property_secondary_logo',
        'property_mono_black_logo',
        'property_mono_white_logo',
      ],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Logo',
    },
  },
};

const emailLinkDef: SlotGroupDefinition = {
  label: 'Email Link',
  fields: {
    url: {
      label: 'Email Address',
      type: 'email',
      defaultField: 'property_email',
    },
    label: {
      label: 'Display Text',
      type: 'text',
      defaultValue: 'Email Us',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ── Social link defs (icons rendered below the logo) ───────────────────────
const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const googleLinkDef: SlotGroupDefinition = {
  label: 'Google Business',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Google' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * Per-instance overrides for ContactBlock. `defaultFormBlockId` /
 * `defaultFormHeading` let a page's section entry pre-select a different
 * embedded form (e.g. `multihub-tour-scheduler` on the Schedule A Tour
 * page) without forking the component. Both fall through to the
 * `contactblock.form_block` / `contactblock.form_heading` schema defaults
 * when omitted, so all other pages keep the standard lead-capture form.
 *
 * Editor-saved overrides still win over these props — the runtime resolves
 * stored values first, then the hook defaults supplied here.
 */
interface ContactBlockProps {
  defaultFormBlockId?: string;
  defaultFormHeading?: string;
}

export function ContactBlock({
  defaultFormBlockId,
  defaultFormHeading,
}: ContactBlockProps = {}) {
  // ── Hero image ──────────────────────────────────────────────────────────
  const heroImage = useSlotGroup('contactblock.hero_image', heroImageDef);

  // ── Form block (left, overlaps hero) ────────────────────────────────────
  const formSlot = useBlockSlot('contactblock.form_block', {
    defaultValue: defaultFormBlockId || 'multihub-lead-form',
  });
  const formHeadingDefault = defaultFormHeading || "We'd love to hear from you!";

  // ── Heading / blurb (right, overlaps hero) ──────────────────────────────
  // Headings/preheading/form-heading are richtext so editors can apply
  // inline emphasis (e.g. <em>, <strong>) and embed property fields.

  // ── Logo (lower container) ──────────────────────────────────────────────
  // Uses the property's standard (primary) logo. No contrast-driven swap
  // between mono variants — the card sits on a static light gray and the
  // standard logo is expected to be legible there.
  const logo = useSlotGroup('contactblock.logo', logoDef);

  // ── Social links (rendered below the logo) ──────────────────────────────
  const facebook = useSlotGroup('contactblock.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('contactblock.instagram_link', instagramLinkDef);
  const google = useSlotGroup('contactblock.google_link', googleLinkDef);
  const hasAnySocial = Boolean(facebook.url || instagram.url || google.url);

  // ── Address / phone / email / hours (lower container) ───────────────────
  const address = useContentToken('contactblock.address');
  const phone = useContentToken('contactblock.phone') || '';
  const emailLink = useSlotGroup('contactblock.email', emailLinkDef);

  const officeHours = useCollectionBinding<OfficeHourEntry>('contactblock.office_hours', {
    collection: 'office_hours',
  });

  const phoneDisplay = phone ? formatDisplayPhone(phone) : '';
  const hasHours = officeHours.length > 0;
  const hoursRows = hasHours ? condenseOfficeHours(officeHours) : [];

  return (
    <section className="cb-section">
      {/* ── HERO IMAGE (full width, white page bg around) ──────────────── */}
      <EditableSlotGroup groupPath="contactblock.hero_image" definition={heroImageDef}>
        <div className="cb-hero-image-wrap">
          {heroImage.image ? (
            <VibesitesImage
              url={heroImage.image}
              alt={heroImage.alt || ''}
              className="cb-hero-image"
            />
          ) : (
            <div className="cb-hero-image-fallback" aria-hidden="true" />
          )}
        </div>
      </EditableSlotGroup>

      {/* ── GRAY CARD: holds form + callout + lower row ────────────────── */}
      <div className="cb-card-wrap">
        <div className="cb-card">
          <div className="cb-overlap-grid">

            {/* Form */}
            <div className="cb-form-col">
              <RichTextContent
                path="contactblock.form_heading"
                defaultValue={formHeadingDefault}
                as="h3"
                className="cb-form-heading"
              />
              <EditableToken path="contactblock.form_block" as="div">
                {formSlot ? (
                  <div className="cb-form-wrap">
                    <SectionContextProvider sectionId={formSlot.sectionId}>
                      <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                    </SectionContextProvider>
                  </div>
                ) : (
                  <div className="cb-form-empty">
                    <p>No form block selected</p>
                  </div>
                )}
              </EditableToken>
            </div>

            {/* Right column: callout (overlaps hero) + logo|info row */}
            <div className="cb-info-col">
              {/* Primary-color heading + blurb square (overlaps hero) */}
              <div className="cb-callout">
                <RichTextContent
                  path="contactblock.preheading"
                  as="p"
                  className="cb-callout-preheading"
                />
                <RichTextContent
                  path="contactblock.heading"
                  as="h2"
                  className="cb-callout-heading"
                />
                <RichTextContent
                  path="contactblock.blurb"
                  defaultValue="<p>Connect with our leasing team to schedule a tour, ask a question, or just say hello. We'd love to hear from you.</p>"
                  as="div"
                  className="cb-callout-blurb"
                />
              </div>

              {/* Below callout: logo (left) | vertical divider | contact info (right).
                  Sits in the right column under the callout, content centered. */}
              <div className="cb-lower-grid">
                <div className="cb-logo-col">
                  <EditableSlotGroup groupPath="contactblock.logo" definition={logoDef}>
                    <div className="cb-logo-wrap">
                      {logo.image ? (
                        <VibesitesImage
                          url={logo.image}
                          alt={logo.alt || 'Property Logo'}
                          className="cb-logo"
                        />
                      ) : (
                        <div className="cb-logo-fallback" aria-hidden="true" />
                      )}
                    </div>
                  </EditableSlotGroup>

                  {hasAnySocial && (
                    <div className="cb-social-row" role="list" aria-label="Social links">
                      {facebook.url && (
                        <EditableSlotGroup groupPath="contactblock.facebook_link" definition={facebookLinkDef}>
                          <a
                            href={facebook.url}
                            target={facebook._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={facebook.label || 'Facebook'}
                            className="cb-social-link"
                            role="listitem"
                          >
                            <FontAwesomeIcon icon={faFacebook} className="cb-social-icon" />
                          </a>
                        </EditableSlotGroup>
                      )}
                      {instagram.url && (
                        <EditableSlotGroup groupPath="contactblock.instagram_link" definition={instagramLinkDef}>
                          <a
                            href={instagram.url}
                            target={instagram._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={instagram.label || 'Instagram'}
                            className="cb-social-link"
                            role="listitem"
                          >
                            <FontAwesomeIcon icon={faInstagram} className="cb-social-icon" />
                          </a>
                        </EditableSlotGroup>
                      )}
                      {google.url && (
                        <EditableSlotGroup groupPath="contactblock.google_link" definition={googleLinkDef}>
                          <a
                            href={google.url}
                            target={google._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={google.label || 'Google'}
                            className="cb-social-link"
                            role="listitem"
                          >
                            <FontAwesomeIcon icon={faGoogle} className="cb-social-icon" />
                          </a>
                        </EditableSlotGroup>
                      )}
                    </div>
                  )}
                </div>

                <div className="cb-info-list" style={{ color: TEXT_ON_CARD }}>
                  {/* Address */}
                  <div className="cb-info-item">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="cb-info-icon"
                      aria-hidden="true"
                    />
                    <EditableToken
                      path="contactblock.address"
                      as="p"
                      className="cb-info-value"
                    >
                      {address || ''}
                    </EditableToken>
                  </div>

                  {/* Phone */}
                  <div className="cb-info-item">
                    <FontAwesomeIcon
                      icon={faMobileScreenButton}
                      className="cb-info-icon"
                      aria-hidden="true"
                    />
                    <p className="cb-info-value">
                      {phone ? (
                        <a
                          href={`tel:${phone}`}
                          className="dniphone dniphonehref cb-info-link"
                        >
                          <EditableToken path="contactblock.phone" as="span">
                            {phoneDisplay}
                          </EditableToken>
                        </a>
                      ) : (
                        <EditableToken path="contactblock.phone" as="span">
                          {phoneDisplay}
                        </EditableToken>
                      )}
                    </p>
                  </div>

                  {/* Email */}
                  <EditableSlotGroup groupPath="contactblock.email" definition={emailLinkDef}>
                    <div className="cb-info-item">
                      <FontAwesomeIcon
                        icon={faPaperPlane}
                        className="cb-info-icon"
                        aria-hidden="true"
                      />
                      <p className="cb-info-value">
                        {emailLink.url ? (
                          <a
                            href={`mailto:${emailLink.url}`}
                            target={emailLink._target || '_self'}
                            className="cb-info-link"
                          >
                            {emailLink.label || emailLink.url}
                          </a>
                        ) : (
                          <span>{emailLink.label || ''}</span>
                        )}
                      </p>
                    </div>
                  </EditableSlotGroup>

                  {/* Office hours */}
                  {hasHours && (
                    <div className="cb-info-item cb-info-item-hours">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="cb-info-icon"
                        aria-hidden="true"
                      />
                      <div className="cb-info-value">
                        <div className="cb-hours-grid">
                          {hoursRows.map((row, i) => (
                            <div key={i} className="cb-hours-row">
                              <span className="cb-hours-day">{row.dayLabel}</span>
                              <span className="cb-hours-time">{row.timeLabel}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .cb-section {
          background: var(--vibe-color-background, #ffffff);
          color: var(--vibe-color-text);
          padding-bottom: 1.5rem;
        }
        @media (min-width: 768px) {
          .cb-section { padding-bottom: 2.5rem; }
        }

        /* ============================================================
           HERO IMAGE — full width, sits on the white page background
           (no gray panel under it)
           ============================================================ */
        .cb-hero-image-wrap {
          position: relative;
          width: 100%;
          height: 200px;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .cb-hero-image-wrap { height: 260px; }
        }
        @media (min-width: 992px) {
          .cb-hero-image-wrap { height: 320px; }
        }

        /* ============================================================
           GRAY CARD — wraps form + callout + lower row.
           Margins around the card show the white page background on
           every edge; internal padding gives breathing room from the
           gray edges to the content.
           ============================================================ */
        .cb-card-wrap {
          padding: 0 1.25rem;
          margin-top: 1.5rem;
        }
        @media (min-width: 768px) {
          .cb-card-wrap {
            padding: 0 2rem;
            margin-top: 2rem;
          }
        }
        @media (min-width: 1200px) {
          .cb-card-wrap {
            padding: 0 3rem;
          }
        }

        /* Gray card background is pinned to the same static light gray
           the Header's upper utility row uses (#f3f4f6). NOT a theme
           token — this color should NOT shift with the property's
           surface/background tokens, by explicit request to match the
           top of the page. See Header.tsx (.oasis-util-row) for the
           sibling usage. */
        .cb-card {
          background: #f3f4f6;
          max-width: 80rem;
          margin: 0 auto;
          padding: 2.5rem 1.5rem;
        }
        @media (min-width: 768px) {
          .cb-card { padding: 3.5rem 2.5rem; }
        }
        @media (min-width: 992px) {
          .cb-card { padding: 4rem 3rem; }
        }

        .cb-hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .cb-hero-image-fallback {
          width: 100%;
          height: 100%;
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            var(--vibe-color-secondary) 100%
          );
        }

        .cb-overlap-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 992px) {
          /* Form column is narrower than the callout so the form feels less
             dominant; callout takes the wider share on the right. */
          .cb-overlap-grid {
            grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
            gap: 2.5rem;
            align-items: start;
          }
        }

        /* Form column — sits transparent on the gray card. No drop
           shadow, no border-radius, no wrapper background. */
        .cb-form-col { width: 100%; }

        /* Heading above the form. Uses the theme heading font and the
           theme primary color. */
        .cb-form-heading {
          font-family: var(--vibe-font-heading);
          color: var(--vibe-color-text-on-primary);
          font-size: 1.25rem;
          line-height: 1.25;
          font-weight: 600;
          letter-spacing: 0.01em;
          margin: 0 0 1rem 0;
        }
        @media (min-width: 768px) {
          .cb-form-heading {
            font-size: 1.5rem;
            margin-bottom: 1.25rem;
          }
        }

        .cb-form-wrap {
          background: transparent;
        }
        /* The selected form block (e.g. multihub-lead-form) renders its
           own "card" div with inline styles: white background, shadow,
           padding, and an <h3> title. Strip those so the form blends
           into the gray cb-card, and hide the form's heading. */
        .cb-form-wrap > div {
          background: transparent !important;
          background-color: transparent !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          padding: 0 !important;
        }
        .cb-form-wrap > div > h3 {
          display: none !important;
        }
        /* Hide the empty spacer div the form renders when it has a
           header but no subheader (inline style marginBottom: 1.5rem). */
        .cb-form-wrap > div > div[style*="marginBottom"] {
          display: none !important;
        }
        /* Keep the success-state overlay matching the gray card. */
        .cb-form-wrap .mh-lead-form-success {
          background: #f3f4f6 !important;
        }
        /* Stack first/last name vertically at full form width. The form
           renders these as a 2-col grid via inline styles; collapse it
           to a single column. The existing 1rem grid gap becomes the
           vertical spacing between the stacked fields. */
        .cb-form-wrap form > div:has(> div > input[name="first_name"]) {
          grid-template-columns: 1fr !important;
        }
        /* Body-text treatment for form fields — match the rest of the
           page (Nunito Sans body font, 14px / 0.875rem). The form block sets
           font-size:1rem inline on inputs/select/textarea, so override
           with !important. Placeholders inherit font-size from the
           field. font-family is already var(--vibe-font-body) in the
           vendor block. */
        .cb-form-wrap input,
        .cb-form-wrap select,
        .cb-form-wrap textarea {
          font-family: var(--vibe-font-body) !important;
          font-size: 0.875rem !important;
        }
        /* Opt-in / consent text — already small (0.8125rem) but pin the
           font to body for safety. */
        .cb-form-wrap label,
        .cb-form-wrap label span {
          font-family: var(--vibe-font-body) !important;
        }
        /* Submit button: half the form width, left-aligned, and the
           visible label is swapped to "SUBMIT" (uppercase) via a
           pseudo-element. The real text node ("Send Message" from the
           block's default content) is collapsed with font-size:0; the
           ::before paints the new label. The disabled state (form is
           submitting) swaps to "SUBMITTING..." to match the form's
           original loading affordance.
           Note: the DOM text node remains the block's content value, so
           the button's accessible name is whatever the form block
           supplies. To make screen-reader output match exactly, also
           edit the block's "Button Text" field in the page editor. */
        .cb-form-wrap form button[type="submit"] {
          width: 50% !important;
          padding: 0.75rem 1.5rem !important;
          font-size: 0 !important;
          letter-spacing: 0 !important;
        }
        .cb-form-wrap form button[type="submit"]::before {
          content: "SUBMIT";
          font-size: 0.9375rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .cb-form-wrap form button[type="submit"]:disabled::before {
          content: "SUBMITTING...";
        }
        .cb-form-empty {
          background: transparent;
          border: 1px dashed var(--vibe-color-border, #e2ddd5);
          padding: 2rem;
          text-align: center;
          color: var(--vibe-color-text-muted);
          font-family: var(--vibe-font-body);
        }

        /* Primary-color heading square — overlaps the top edge of the
           gray card on desktop so it extends upward into the hero image
           area above. Sharp corners, no border-radius. */
        .cb-callout {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #ffffff);
          padding: 4.5rem 1.75rem;
          align-self: start;
          position: relative;
          z-index: 2;
        }
        @media (min-width: 768px) {
          .cb-callout { padding: 6rem 2.5rem; }
        }
        @media (min-width: 992px) {
          /* Pull the callout up so it overlaps:
             - the gray card's top padding (~4rem = 64px)
             - the small white gap between hero and card (~2rem = 32px)
             - and a small portion of the hero image itself (~50px)
             ≈ -150px total */
          .cb-callout { margin-top: -150px; }
        }
        @media (min-width: 1200px) {
          .cb-callout { margin-top: -165px; }
        }
        .cb-callout-preheading {
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          line-height: 1.2;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin: 0 0 1.25rem;
          color: inherit;
          opacity: 0.85;
        }
        @media (min-width: 768px) {
          .cb-callout-preheading {
            font-size: 0.875rem;
            margin-bottom: 1.5rem;
          }
        }
        /* Heading sized down a tier from the previous 1.875rem/2.25rem
           pair so the callout reads with the same weight-and-presence
           hierarchy the rest of the home page now uses (lighter
           headings, heavier weight). Font-weight bumped 600 → 800 so
           the new smaller size still anchors the callout — same
           "smaller but bolder" treatment used elsewhere on the page. */
        .cb-callout-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.5rem;
          line-height: 1.2;
          font-weight: 800;
          letter-spacing: 0.01em;
          margin: 0 0 1.5rem;
          color: var(--vibe-color-secondary);
        }
        @media (min-width: 768px) {
          .cb-callout-heading {
            font-size: 1.75rem;
            margin-bottom: 1.75rem;
          }
        }
        /* Blurb sits one tier below the heading — comfortable body
           copy size (0.9375rem / 1rem) so the paragraph reads as
           supporting text rather than competing with the heading.
           Earlier revision briefly pinned this to the heading's size;
           reverted because it overpowered the callout. */
        .cb-callout-blurb {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          /* 1.92 to match the theme-wide body line-height that mirrors
             https://www.lakehausapartmenthomes.com/ . */
          line-height: 1.92;
          color: inherit;
          opacity: 0.92;
        }
        @media (min-width: 768px) {
          .cb-callout-blurb {
            font-size: 1rem;
          }
        }
        .cb-callout-blurb p:last-child { margin-bottom: 0; }

        /* ============================================================
           RIGHT COLUMN — callout sits at the top (overlapping hero),
           lower-grid (logo | divider | info) stacks under it.
           ============================================================ */
        .cb-info-col {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }
        @media (min-width: 768px) {
          .cb-info-col { gap: 2.5rem; }
        }

        /* Lower grid lives inside the right column. Max-width keeps the
           row compact; on mobile/tablet it stays centered, on desktop it
           shifts toward the left edge of the column (and slightly into
           the column gap) so the logo reads closer to the page center. */
        .cb-lower-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.75rem;
          align-items: center;
          max-width: 560px;
          margin: 0 auto;
          width: 100%;
        }
        @media (min-width: 640px) {
          .cb-lower-grid {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 2.5rem;
            align-items: center;
            position: relative;
          }
          /* Vertical divider sits between the two columns */
          .cb-lower-grid::before {
            content: '';
            position: absolute;
            top: 0.5rem;
            bottom: 0.5rem;
            left: 50%;
            width: 1px;
            background: var(--vibe-color-border, #e2ddd5);
            transform: translateX(calc(-1.25rem - 0.5px));
          }
        }
        @media (min-width: 992px) {
          /* Pull the row toward the page center: left-align inside the
             right column and nudge it further left into the column gap. */
          .cb-lower-grid {
            margin-left: -2.5rem;
            margin-right: auto;
          }
        }

        /* Logo column — logo on top, social icon row below. */
        .cb-logo-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
        }
        @media (min-width: 768px) {
          .cb-logo-col { gap: 1.75rem; }
        }

        /* Logo — centered within its half of the lower grid.
           Logo fills the available column up to its natural aspect ratio,
           bounded by max-height so very tall logos don't crowd the gray
           card's vertical edges. The wrapper adds a small inset so the
           logo never quite touches the column's outer edges (the divider
           on one side, the card padding / column gap on the others). */
        .cb-logo-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 0.75rem 0.75rem;
        }
        @media (min-width: 768px) {
          .cb-logo-wrap { padding: 1rem 1rem; }
        }
        .cb-logo {
          width: 100%;
          max-width: 100%;
          height: auto;
          /* Mobile: stack layout, logo can grow tall (up to ~card padding). */
          max-height: 220px;
          object-fit: contain;
          display: block;
        }
        @media (min-width: 768px) {
          /* Tablet: 2-col lower grid kicks in at 640px; tablet card padding
             gives plenty of vertical room. */
          .cb-logo { max-height: 260px; }
        }
        @media (min-width: 992px) {
          /* Desktop: logo column is ~260px wide; allow a near-square logo
             box without crowding the divider above/below. */
          .cb-logo { max-height: 280px; }
        }
        .cb-logo-fallback {
          width: 100%;
          max-width: 220px;
          height: 80px;
          /* Matches the card background (#f3f4f6) so the empty-state
             placeholder stays invisible against the gray card. */
          background: #f3f4f6;
          border-radius: var(--vibe-radius-md, 0.25rem);
        }

        /* Social icon row — sits directly under the logo */
        .cb-social-row {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          gap: 0.15rem;
        }
        @media (min-width: 768px) {
          .cb-social-row { gap: 0.25rem; }
        }
        .cb-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.75rem;
          height: 1.75rem;
          border: 0;
          background: transparent;
          color: var(--vibe-color-primary);
          text-decoration: none;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .cb-social-link:hover,
        .cb-social-link:focus-visible {
          color: color-mix(in srgb, var(--vibe-color-primary) 75%, black);
          transform: translateY(-1px);
        }
        .cb-social-link:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .cb-social-icon {
          width: 1.1rem;
          height: 1.1rem;
        }

        /* Vertical contact-info stack (right side of lower grid) */
        .cb-info-list {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        .cb-info-item {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0.75rem;
        }
        /* Hours wraps to multiple rows — top-align the clock icon. */
        .cb-info-item-hours {
          align-items: flex-start;
        }
        .cb-info-item-hours .cb-info-icon {
          margin-top: 0.25rem;
        }
        .cb-info-icon {
          width: 1rem;
          height: 1rem;
          color: var(--vibe-color-primary);
          flex-shrink: 0;
        }
        .cb-info-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          /* 1.92 matches the theme-wide body line-height — addresses
             with multiple lines (street / city-state-zip) now breathe
             at the same rhythm as the rest of the site's body copy. */
          line-height: 1.92;
          /* Color comes from the .cb-info-list wrapper's inline style
             (TEXT_ON_CARD = #1a1a1a). Children inherit so the whole
             stack stays in sync. */
          color: inherit;
          margin: 0;
        }
        .cb-info-link {
          color: inherit;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .cb-info-link:hover { color: var(--vibe-color-primary); }

        .cb-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }
        /* Day label and time sit close together on the left, not pushed
           to opposite ends of the row. */
        .cb-hours-row {
          display: flex;
          justify-content: flex-start;
          align-items: baseline;
          gap: 0.6rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          /* 1.92 matches the theme-wide body line-height — see
             .cb-info-value above; hours rows pick up the same airy
             rhythm so the contact column's vertical cadence is
             consistent. */
          line-height: 1.92;
        }
        .cb-hours-day {
          color: inherit;
          font-weight: 500;
        }
        .cb-hours-time {
          /* Same color + weight as the address/phone/email and the day
             label — full-opacity inherited contrast color, default 400
             body weight (matches .cb-info-value). */
          color: inherit;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactBlock',
  displayName: 'Contact Block',
  tokens: {
    'contactblock.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactblock.form_heading': {
      label: 'Form Heading',
      type: 'richtext',
      defaultValue: "We'd love to hear from you!",
    },
    'contactblock.preheading': {
      label: 'Preheading',
      type: 'richtext',
      defaultValue: 'Get in Touch',
    },
    'contactblock.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: 'Connect With Us Today',
    },
    'contactblock.blurb': {
      label: 'Blurb',
      type: 'richtext',
      defaultValue:
        "<p>Connect with our leasing team to schedule a tour, ask a question, or just say hello. We'd love to hear from you.</p>",
    },
    'contactblock.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    'contactblock.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
  },
  slotGroups: {
    'contactblock.hero_image': heroImageDef,
    'contactblock.logo': logoDef,
    'contactblock.email': emailLinkDef,
    'contactblock.facebook_link': facebookLinkDef,
    'contactblock.instagram_link': instagramLinkDef,
    'contactblock.google_link': googleLinkDef,
  },
  collectionBindings: {
    'contactblock.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactBlock as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactBlock;
